import { useCallback } from 'react';
import { observer } from 'mobx-react-lite';
import { AvailabilityCalendar, type CalendarDay } from '@link/share/booking';
import { isPlanDayAvailable } from '@link/share/hooks/productPageSelectors';
import { CAPABILITIES } from '../../../../capabilities';
import { useBookingStore } from '../context';
import styles from './plans.module.css';

interface Props {
  planId: string;
  /** Which days can be picked; the plan's own stock when omitted. */
  isDayBookable?: (day: CalendarDay | undefined) => boolean;
  /** Draw a border round the calendar, where nothing else frames it. */
  framed?: boolean;
  onLoadMore: () => void;
}

/** The plan's date picker. */
export const PlanCalendar = observer(function PlanCalendar({
  planId,
  isDayBookable,
  framed,
  onLoadMore
}: Props) {
  const store = useBookingStore();
  const { calendar } = store;
  const isOpenForPlan = useCallback(
    (day: CalendarDay | undefined) => isPlanDayAvailable(day, planId),
    [planId]
  );
  const picker = (
    <AvailabilityCalendar
      days={store.calendarDays}
      value={store.planStates.get(planId)?.selectedDate ?? ''}
      isDayBookable={isDayBookable ?? isOpenForPlan}
      paging={{
        loadedUntil: calendar.loadedUntil,
        hasMore: calendar.hasMore,
        isLoadingMore: calendar.isLoadingMore,
        onLoadMore
      }}
      hideFinalSoldOutMonthMarks={!CAPABILITIES.lastMonthSoldOut}
      onSelect={date => store.selectDate(planId, date)}
    />
  );
  return framed ? <div className={styles.frame}>{picker}</div> : picker;
});
